import { Fade } from '../../webit_modules/wb-barba-transitions/src';
import barba from '@barba/core';
import { WbDataLayers } from './WbDataLayers';

export class WbBarbaController {
  constructor() {
    this.objects = [];
    this.dataLayers = new WbDataLayers();
    this.keepScript = '#theme-js';
    this.dataLayers.init();
  }

  addObject(fn) {
    this.objects.push(fn);
  }

  async init() {
    this.initBarbaRouting();
    this.initAllFunctions();
    await this.dataLayers.run(window.location.pathname);

    this.updateCSRFTokens();

    if (WbCraftCommmerce) {
      window.WbCommerce.updateCartQty();
    }
  }

  initFunction(fn) {
    return new Promise(resolve => {
      fn.init().then(resolve);
    });
  }

  initAllFunctions() {
    const _this = this;

    return new Promise(resolve => {
      Promise.all(_this.objects.map(_this.initFunction)).then(resolve);
    });
  }

  initBarbaRouting() {
    const _this = this;
    const ignoredUrls = [
      '/achat/panier',
      '/achat/adresses',
      '/achat/expedition',
      '/achat/methode-paiement',
      '/achat/paiement',
      '/checkout/cart',
      '/checkout/addresses',
      '/checkout/shipping',
      '/checkout/payment-method',
      '/checkout/payment',
      '/index.php'
    ];

    barba.init({
      timeout: 30000,
      debug: true, // Switch to false when compiling for prod
      preventRunning: true,
      cacheIgnore: ignoredUrls,
      prefetchIgnore: ignoredUrls,
      schema: {
        prefix: 'data-transition',
      },
      /**
       * Lifecycle documentation - https://barba.js.org/docs/getstarted/lifecycle/
       */
      transitions: [
        {
          name: 'default',
          leave(data) {
            return new Promise(resolve => {
              const transition = new Fade();

              document.querySelector('html').classList.add('wb-transitioning');

              if (typeof a11y != 'undefined' && !a11y.data.anims) resolve();
              else transition.in().then(resolve);
            });
          },

          barbaBeforeEnter(data) {
            const _this = this;

            return new Promise(resolve => {
              _this
                .initAllFunctions()
                .then(() => {
                  WbMasthead.resize(true);
                  document.querySelectorAll('html, body').forEach(e => e.classList.remove('--locked'));
                  document.querySelector('html').focus();
                })
                .then(resolve);
            });
          },
          enter(data) {
            return new Promise(resolve => {
              const transition = new Fade();
              document.dispatchEvent(new Event('_before_page_loaded'));
              document.dispatchEvent(new Event('_page_loaded'));

              WbScrollTo.scrollTo({
                element: 'body',
                duration: 0,
                offset: 0,
              });

              if (typeof a11y != 'undefined' && !a11y.data.anims) resolve();
              else transition.out().then(resolve);
            });
          },

          after(data) {
            return new Promise(resolve => {
              _this.dataLayers.run(data.next.url.path);
              document.querySelector('html').classList.remove('wb-transitioning');
              WbScrollTo.scrollToAnchor().then(resolve);
            });
          },
        },
      ],

      prevent: function(data) {
        const isClickEvent = data.event.type === 'click';
        const currentUrl = window.location.href.split('#')[0];
        const destinationIsCurrent = currentUrl === data.href;

        if (isClickEvent && destinationIsCurrent) {
          data.event.preventDefault();
          WbScrollTo.scrollTo({
            element: 'body',
            offset: 0,
          });
        }

        //console.log(data);
        if (data.el.classList.contains('ab-item') || data.el.classList.contains('data-barba-prevent') || data.href?.indexOf('actions/formie/submissions')!==false) {
          return true;
        }
      },

      // TODO: This
      requestError: (trigger, action, url, response) => {
        // go to a custom 404 page if server responds with a 404 response status
        if (action === 'click' && response.status && response.status === 404) {
          barba.go('/404');
        }

        return false;
      },
    });

    barba.hooks.afterLeave(data => {
      return new Promise(resolve => {
        _this.barbaAfterLeave(data).then(resolve);
      });
    });

    barba.hooks.beforeEnter(data => {
      return new Promise(resolve => {
        data?.current?.container?.remove();
        _this.barbaBeforeEnter(data).then(resolve);
      });
    });

    /**
     * Use this hook for Google Analytics
     * Remove if using GTM and configure history change - https://bookstack.webitinteractive.ca/books/gtm---barbajs
     */
    // barba.hooks.after(function (data) {
    //   if (typeof gtag != 'undefined') {
    //     gtag('config', 'UA-XXXXXXXXX-X', { 'page_path': data.next.url.path });
    //   } else if (typeof ga != 'undefined') {
    //     ga('set', 'page', data.next.url.path);
    //     ga('send', 'pageview');
    //   }
    // });
  }

  barbaAfterLeave(data) {
    const _this = this;

    return new Promise(resolve => {
      WbMasthead.closeMenu();
      _this.updateExternalScripts(data);
      _this.updateNavs(data);

      resolve();
    });
  }

  barbaBeforeEnter(data) {
    const _this = this;

    return new Promise(resolve => {
      this.updateCSRFTokens();

      _this
        .initAllFunctions()
        .then(() => {
          WbMasthead.resize(true);
          document.querySelectorAll('html, body').forEach(e => e.classList.remove('--locked'));

          document.querySelector('html').focus();
        })
        .then(resolve);
    });
  }

  updateExternalScripts(data) {
    const nextContainer = data.next.container.parentNode.parentNode;
    const currentRef = document.querySelector('#page');
    const newRef = nextContainer.querySelector('#page');
    const newScripts = newRef._nextAll('script, link')._not(this.keepScript);
    const currentScripts = currentRef._nextAll('script, link')._not(this.keepScript);

    // Change meta tags and seomatic graph
    document.querySelectorAll('head meta')?._remove();
    document.querySelector('head')?._prependAll(nextContainer.querySelectorAll('meta:not([itemprop]'));
    document.querySelectorAll('link[rel="alternate"]')?.forEach(link => {
      const hreflang = link.getAttribute('hreflang');
      link?.replaceWith(nextContainer.querySelector(`link[hreflang="${hreflang}"]`));
    });

    // Remove duplicate recaptcha scripts from head
    document.querySelectorAll('script[src^="https://www.gstatic.com/recaptcha/"]')?._remove();

    // Remove plugin-specific scripts from the old DOM
    currentScripts.forEach(e => {
      e._remove();
    });

    // Add plugin-specific scripts to the new DOM
    const scripts = new Set([...newScripts]);
    this.createScripts(scripts, currentRef);

    // Dispatch DOMContentLoaded for plugins and ReCaptcha init
    document.dispatchEvent(new Event('DOMContentLoaded'));
  }

  updateNavs(data) {
    const nextContainer = data.next.container.parentNode.parentNode;
    const hasGoogleTranslate = nextContainer.querySelector('#google_translate_element');

    document.querySelector('#mastfoot').replaceWith(nextContainer.querySelector('#mastfoot'));

    if (WbCraftCommmerce) {
      window.WbCommerce.updateCartQty();
      if (!hasGoogleTranslate) {
        document.querySelector('#masthead').replaceWith(nextContainer.querySelector('#masthead'));
      }
    }

    const theme = document.querySelector('#masthead').getAttribute('data-theme');
    document.querySelector('html').setAttribute('data-theme', theme);
    document.querySelector('body').className = 'wb-theme__' + theme;

    if (typeof data.trigger !== 'string' && data.trigger.classList.contains('language-switcher')) {
      const lang = data.trigger.getAttribute('lang');
      document.documentElement.setAttribute('lang', lang);
      document.querySelector('link[rel="home"]')?.replaceWith(nextContainer.querySelector('link[rel="home"]'));

      if (!WbCraftCommmerce && !hasGoogleTranslate) {
        document.querySelector('#masthead').replaceWith(nextContainer.querySelector('#masthead'));
      }
    }
  }

  createScripts(scripts, ref) {
    scripts.forEach(e => {
      const scriptCode = e.innerHTML;
      const newScript = document.createElement('script');

      Array.from(e.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));

      if (scriptCode) newScript.appendChild(document.createTextNode(scriptCode));

      document.querySelector('body')._insertAfter(newScript, ref);
    });
  }

  updateCSRFTokens() {
    const tokens = document.querySelectorAll('[name=CRAFT_CSRF_TOKEN]');

    if (tokens) {
      fetch('/actions/blitz/csrf/token')
        .then(result => {
          return result.text();
        })
        .then(result => {
          tokens.forEach(token => (token.value = result));
        });
    }
  }
}
